import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth, ROLE_HOME, type AppRole } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireRole?: AppRole;
  requireRoles?: AppRole[];
  /** If true, do not enforce onboarding gate (e.g. on /onboarding itself). */
  skipOnboarding?: boolean;
}

export default function ProtectedRoute({
  children,
  requireAdmin = false,
  requireRole,
  requireRoles,
  skipOnboarding = false,
}: ProtectedRouteProps) {
  const { user, isAdmin, role, loading } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const notifiedRef = useRef(false);

  const allowedRoles = requireRoles ?? (requireRole ? [requireRole] : undefined);
  const needsRoleCheck = requireAdmin || !!allowedRoles;
  // If a role check is required but the role hasn't loaded yet, keep waiting
  // instead of flashing an "Unauthorized" screen.
  const roleStillResolving = !loading && !!user && needsRoleCheck && role === null;

  const wrongRole =
    !loading && !!user && role !== null && (
      (requireAdmin && !isAdmin) ||
      (!!allowedRoles && !allowedRoles.includes(role))
    );

  useEffect(() => {
    if (notifiedRef.current) return;
    if (wrongRole) {
      notifiedRef.current = true;
      toast({ variant: "destructive", title: "Unauthorized Access", description: "You don't have permission to view this page." });
    }
  }, [wrongRole, toast]);

  if (loading || roleStillResolving) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    // Silent redirect — no toast during normal logout or first visit.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (wrongRole) {
    const home = role ? ROLE_HOME[role] : "/dashboard";
    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
}
